import React, { Fragment, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

// Components
import RadioBox from './RadioBox'
import ButtonBox from './ButtonBox'

// UI
import BRadioGroup from 'ui/blocks/RadioGroup'
import Icon from 'ui/components/Icon'
import Div from 'ui/elements/Div'
import BInputField from 'ui/blocks/InputField'

const RadioGroup = ({
  options,
  field,
  exclusive,
  type,
  form,
  size,
  displayGrid,
  bordered,
  errorOnSubmit,
  dataTid,
  modifiers,
  initialError,
  error,
  touched,
  hideErrorLabel,
  description,
  label
}) => {
  const handleChange = useCallback((value) => {
    field.onChange(value)

    setTimeout(() => {
      form && form.setFieldTouched(field.name, true, true)
    }, 1)
  }, [field, form])

  const renderOptions = useMemo(() => {
    if (type === 'buttonGroup') {
      return (
        <BRadioGroup.Buttons>
          {options.map(({ renderWrapper, ...option }) => {
            const children = (
              <ButtonBox
                key={option.value}
                size={size}
                {...option}
                checked={field.value === option.value}
                onClick={() => handleChange(option.value)}
                modifiers={modifiers}
              />
            )

            if (typeof renderWrapper === 'function') {
              return renderWrapper(children, option.value)
            }

            return children
          })}
        </BRadioGroup.Buttons>
      )
    }

    return options.map(({ renderWrapper, ...option }) => {
      const children = (
        <RadioBox
          key={option.value}
          {...option}
          checked={field.value === option.value}
          onClick={() => handleChange(option.value)}
          exclusive={exclusive}
          bordered={bordered}
          data-tid={`Checked ${field.value === option.value}`}
          modifiers={modifiers}
        />
      )

      if (typeof renderWrapper === 'function') {
        return renderWrapper(children, option.value)
      }

      return children
    })
  }, [options, field.value, handleChange, type, size, bordered, exclusive, modifiers])

  const renderDescription = useMemo(() => {
    if (!description) return null

    // If description is a function, call it with the field value
    const content = typeof description === 'function' ? description(field.value) : description

    return (
      <Div modifiers="margin-top-2">
        {content}
      </Div>
    )
  }, [description, field.value])

  const renderLabel = useMemo(() => {
    if (!label) return null

    return (
      <BInputField.Label>
        {label}
      </BInputField.Label>
    )
  }, [label])

  const formDirty = form?.dirty
  const hasSubmitted = form?.submitCount > 0
  const displayError = (errorOnSubmit && error && hasSubmitted) ||
    (initialError && formDirty && error) ||
    (hasSubmitted && !touched && error)

  const nestedModifiers = typeof modifiers === 'object' ? modifiers : { self: modifiers }

  return (
    <Fragment>
      {renderLabel}
      <BRadioGroup
        type={type}
        centered={false}
        error={displayError && error}
        bordered={bordered}
        data-tid={dataTid}
        modifiers={nestedModifiers.self}
        displayGrid={displayGrid}
      >
        {renderOptions}
        {displayError && !hideErrorLabel && (
          <BRadioGroup.Error>
            <Icon icon="exclamation-triangle" style="far" /> {error}
          </BRadioGroup.Error>
        )}
      </BRadioGroup>
      {renderDescription}
    </Fragment>
  )
}

RadioGroup.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    label: PropTypes.any.isRequired,
    description: PropTypes.any,
    children: PropTypes.node
  })).isRequired,
  field: PropTypes.object.isRequired,
  exclusive: PropTypes.bool,
  type: PropTypes.string,
  form: PropTypes.object,
  size: PropTypes.string,
  displayGrid: PropTypes.any,
  bordered: PropTypes.bool,
  errorOnSubmit: PropTypes.bool,
  dataTid: PropTypes.string,
  modifiers: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  initialError: PropTypes.any,
  error: PropTypes.any,
  touched: PropTypes.any,
  hideErrorLabel: PropTypes.any,
  description: PropTypes.any,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
}

export default RadioGroup
